import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import {
    mapAgeToText,
    mapGenderToText
} from '../../mappers/log/multipliers/multipliers';
import { Bem } from '../../service/Bem/Bem';
import { LogHelpers } from '../../service/LogHelpers/LogHelpers';
import {
    ChangedOptions,
    DemographyMultiplierValue,
    LogTypenames,
    MultipliersGroups,
    MultiplierValue,
    RetargetingValue
} from '../../typings/log';
import {
    ExpandableCell,
    ExpandableProps
} from '../ExpandableCell/ExpandableCell';

import './MultipliersCell.css';
import * as keyset from './MultipliersCell.i18n';

const i18n = i18nFactory(keyset);

const noCorrectionValue = LogHelpers.noCorrectionValue;

export interface MultipliersProps extends ExpandableProps {
    diff: MultipliersGroups;
    type: LogTypenames;
}

export class MultipliersCell extends ExpandableCell<MultipliersProps> {
    protected getContent() {
        return (
            <div
                className={Bem.cls('multipliers-cell')}
                ref={ref => (this.cell = ref)}
            >
                {this.getMultipliersDiff()}
            </div>
        );
    }

    private getMultipliersDiff = (): React.ReactNode => {
        const {
                diff: { value }
            } = this.props,
            sorted = this.sortMultipliers(value);

        return (
            <div>
                <div
                    key={'after'}
                    className={classNames(Bem.cls('multipliers-cell', 'row'))}
                >
                    {sorted.map((val: MultiplierValue, index) => {
                        return this.getMultiplier(val, index);
                    })}
                </div>
            </div>
        );
    };

    private getMultiplier = (
        value: MultiplierValue,
        index: number
    ): React.ReactNode => {
        const { isExpanded, canExpand } = this.props,
            { type } = value;

        return (
            <div
                key={`multiplier-${index}`}
                className={
                    !isExpanded && canExpand
                        ? Bem.cls('expandable-cell', 'short-row')
                        : ''
                }
            >
                {type === ChangedOptions.modified
                    ? this.getModifiedSpan(value)
                    : this.getSimpleSpan(value)}
            </div>
        );
    };

    private getModifiedSpan = (value: MultiplierValue): React.ReactNode => {
        const { multiplier, compareMultiplier } = value,
            correctionSign = LogHelpers.getSign(multiplier),
            multiplierValue = Math.abs(multiplier - noCorrectionValue),
            compareSign = LogHelpers.getSign(compareMultiplier),
            compareMultiplierValue = Math.abs(
                value.compareMultiplier - noCorrectionValue
            );

        return (
            <span>
                <span className={Bem.cls('multipliers-cell', 'removed')}>
                    {compareSign}
                    {compareMultiplierValue}%
                </span>
                &nbsp;
                <span className={Bem.cls('multipliers-cell', 'added')}>
                    {correctionSign}
                    {multiplierValue}%
                </span>
                &nbsp;
                {this.getConditionName(value)}
            </span>
        );
    };

    private getSimpleSpan = (value: MultiplierValue): React.ReactNode => {
        const { type, multiplier, compareMultiplier } = value,
            correctionSign = LogHelpers.getSign(multiplier),
            multiplierValue = Math.abs(multiplier - noCorrectionValue),
            compareSign = LogHelpers.getSign(compareMultiplier),
            compareMultiplierValue = Math.abs(
                value.compareMultiplier - noCorrectionValue
            );

        if (type === ChangedOptions.removed) {
            return (
                <span className={Bem.cls('multipliers-cell', 'removed')}>
                    {compareSign}
                    {compareMultiplierValue}
                    %&nbsp;
                    {this.getConditionName(value)}
                </span>
            );
        } else {
            return (
                <span
                    className={Bem.cls(
                        'multipliers-cell',
                        Bem.beminize(type || ChangedOptions.notModified)
                    )}
                >
                    {correctionSign}
                    {multiplierValue}
                    %&nbsp;
                    {this.getConditionName(value)}
                </span>
            );
        }
    };

    private getConditionName = (val: MultiplierValue): React.ReactNode => {
        const { type } = this.props;
        let value, condition;
        if (type === LogTypenames.retargeting) {
            value = val as RetargetingValue;
            condition = value.retargetingConditionName.trim();
        } else {
            value = val as DemographyMultiplierValue;
            condition = i18n('gender-and-age', {
                gender: mapGenderToText(value.gender),
                age: mapAgeToText(value.age)
            });
        }

        return (
            <span className={Bem.cls('multipliers-cell', 'condition')}>
                {i18n('on-condition', { condition: condition })}
            </span>
        );
    };

    private sortMultipliers = (
        multipliers: Array<MultiplierValue>
    ): Array<MultiplierValue> => {
        const multiplierWeights = {
            [ChangedOptions.notModified]: 0,
            [ChangedOptions.removed]: 1,
            [ChangedOptions.modified]: 2,
            [ChangedOptions.added]: 3
        };
        return multipliers.sort(
            (val1: MultiplierValue, val2: MultiplierValue) => {
                const val1Type = val1.type || ChangedOptions.notModified,
                    val2Type = val2.type || ChangedOptions.notModified;

                return (
                    multiplierWeights[val2Type] - multiplierWeights[val1Type]
                );
            }
        );
    };
}
